import React, { useEffect, useRef, useMemo } from "react";
import { Chart, registerables } from "chart.js";
import "chartjs-adapter-date-fns";
import datalabels from "chartjs-plugin-datalabels";
import annotation from "chartjs-plugin-annotation";
import cloneDeep from "lodash-es/cloneDeep";
import { useDispatch } from "react-redux";
import { setFileViewerData } from "@/redux/fileViewerSlice";
import { convertRowColToExcelRef } from "@/utils/excelCellUtils";

Chart.register(...registerables, datalabels, annotation);

/**
 * Recursively converts string values that look like JS function definitions
 * into actual functions (e.g., callback strings serialized via json.dumps()).
 */
function reviveFunctions(obj: any): any {
  if (typeof obj === "string") {
    let trimmed = obj.trim();
    // Strip ##...## delimiters that the LLM uses to wrap function strings in JSON
    if (trimmed.startsWith("##") && trimmed.endsWith("##")) {
      trimmed = trimmed.slice(2, -2).trim();
    }
    if (trimmed.startsWith("function") || /^\(.*\)\s*=>/.test(trimmed)) {
      try {
        return new Function("return " + trimmed)();
      } catch {
        return obj;
      }
    }
  }
  if (Array.isArray(obj)) return obj.map(reviveFunctions);
  if (obj && typeof obj === "object") {
    for (const key of Object.keys(obj)) {
      obj[key] = reviveFunctions(obj[key]);
    }
  }
  return obj;
}

/**
 * Recursively strips empty objects ({}) from a chart config. LLMs sometimes
 * generate empty objects for callback fields (e.g., "callback": {}), which
 * crash Chart.js when it tries to call them as functions.
 */
function stripEmptyObjects(obj: any): any {
  if (Array.isArray(obj)) return obj.map(stripEmptyObjects);
  if (obj && typeof obj === "object") {
    for (const key of Object.keys(obj)) {
      obj[key] = stripEmptyObjects(obj[key]);
      if (
        obj[key] &&
        typeof obj[key] === "object" &&
        !Array.isArray(obj[key]) &&
        Object.keys(obj[key]).length === 0
      ) {
        delete obj[key];
      }
    }
  }
  return obj;
}

/**
 * Processes llmData (string or object) into chart data format
 * Returns the processed chart data and metadata, or null if empty/invalid
 */
export const processChartData = (
  llmData: any
): { chartData: any; metadata?: any } | null => {
  let chartData;
  let metadata: any = undefined;

  if (typeof llmData === "string") {
    // XXX: See the issues in `fp-backend/main/v1/QueryResult/services/charts.py`. For now this is how we deal with LLM providing JavaScript.

    // Strip markdown code fence markers if present
    let processedData = llmData.trim();
    if (processedData.startsWith("```json") && processedData.endsWith("```")) {
      processedData = processedData.slice(7, -3).trim(); // Remove ```json from start and ``` from end
    } else if (
      processedData.startsWith("```") &&
      processedData.endsWith("```")
    ) {
      processedData = processedData.slice(3, -3).trim(); // Remove ``` from both ends
    }

    try {
      chartData = eval(`(${processedData});`);
      // XXX: See the issues in `fp-backend/main/v1/QueryResult/services/charts.py`. For now we only assume one chart.
      if (
        chartData?.charts &&
        Array.isArray(chartData.charts) &&
        chartData.charts.length > 0
      ) {
        chartData = chartData.charts[0];
      } else {
        return null;
      }
    } catch (error) {
      console.warn("ChartViewer: Failed to parse chart data:", error);
      return null;
    }
  } else {
    // Check if this is the new format with metadata
    if (llmData && typeof llmData === "object" && "chart" in llmData) {
      // New format: { chart: "...", metadata: {...} }
      metadata = llmData.metadata;
      chartData = llmData.chart;

      // Parse chart if it's a string
      if (typeof chartData === "string") {
        try {
          let processedData = chartData.trim();
          if (
            processedData.startsWith("```json") &&
            processedData.endsWith("```")
          ) {
            processedData = processedData.slice(7, -3).trim();
          } else if (
            processedData.startsWith("```") &&
            processedData.endsWith("```")
          ) {
            processedData = processedData.slice(3, -3).trim();
          }
          // Try JSON.parse first (for valid JSON)
          try {
            chartData = JSON.parse(processedData);
            // JSON.parse succeeds but function strings remain as strings.
            // Convert them to actual functions so Chart.js can call them.
            chartData = reviveFunctions(chartData);
          } catch (jsonError) {
            // If JSON.parse fails, try eval (for JSON with functions)
            // XXX: See the issues in `fp-backend/main/v1/QueryResult/services/charts.py`.
            // This handles LLM providing JavaScript functions in chart config.
            chartData = eval(`(${processedData});`);
            // Extract the first chart from charts array if present
            if (
              chartData?.charts &&
              Array.isArray(chartData.charts) &&
              chartData.charts.length > 0
            ) {
              chartData = chartData.charts[0];
            }
          }
        } catch (error) {
          console.warn("ChartViewer: Failed to parse chart string:", error);
          return null;
        }
      }
    } else {
      // Old format: just chart data
      chartData = cloneDeep(llmData);
    }
  }

  if (!chartData || Object.keys(chartData).length === 0) {
    return null;
  }

  reviveFunctions(chartData);
  stripEmptyObjects(chartData);

  // When a tick callback exists alongside maxTicksLimit, remove maxTicksLimit
  // and disable autoSkip so the callback can control label visibility across
  // all data points. Both maxTicksLimit and autoSkip pre-select tick positions
  // before the callback runs, which can cause the callback's conditional logic
  // to miss the positions it targets (e.g., only December months in a monthly
  // series). To avoid dense grid lines, make them transparent for empty labels.
  if (chartData.options?.scales) {
    for (const scaleConfig of Object.values(
      chartData.options.scales
    ) as any[]) {
      if (
        scaleConfig?.ticks?.callback != null &&
        typeof scaleConfig.ticks.callback === "function" &&
        scaleConfig.ticks.maxTicksLimit != null
      ) {
        delete scaleConfig.ticks.maxTicksLimit;
        scaleConfig.ticks.autoSkip = false;
        const origColor = scaleConfig.grid?.color ?? "#D1D5DB";
        scaleConfig.grid = scaleConfig.grid ?? {};
        scaleConfig.grid.color = (context: any) => {
          if (
            context.tick?.label != null &&
            String(context.tick.label).length > 0
          ) {
            return typeof origColor === "function"
              ? origColor(context)
              : origColor;
          }
          return "transparent";
        };
        scaleConfig.grid.drawTicks = false;
      }
    }
  }

  return { chartData, metadata };
};

const ChartViewer = ({ llmData }: { llmData: any }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const dispatch = useDispatch();

  // Memoize processed chart data to prevent unnecessary re-renders
  // This prevents chart flickering when parent re-renders (e.g., during scrolling)
  const processed = useMemo(() => processChartData(llmData), [llmData]);

  // Check if chart data is empty early - if so, don't render at all
  if (!processed) {
    console.warn("ChartViewer: Received empty chart data:", llmData);
    return null;
  }

  const { chartData, metadata } = processed;

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    // chartData is already validated above, so we can use it directly
    const processedData = cloneDeep(chartData);

    // Filter out null values from datasets to prevent Chart.js errors
    if (processedData.data?.datasets) {
      processedData.data.datasets = processedData.data.datasets.map(
        (dataset: any) => ({
          ...dataset,
          data: dataset.data?.filter((point: any) => point !== null) || [],
        })
      );
    }

    // Ensure options and plugins exist
    processedData.options = processedData.options || {};
    processedData.options.plugins = processedData.options.plugins || {};

    // Hide datasets with empty labels from the legend by default
    processedData.options.plugins.legend =
      processedData.options.plugins.legend || {};
    processedData.options.plugins.legend.labels =
      processedData.options.plugins.legend.labels || {};
    if (!processedData.options.plugins.legend.labels.filter) {
      processedData.options.plugins.legend.labels.filter = (legendItem: any) =>
        legendItem && legendItem.text && legendItem.text.length > 0;
    }

    // ensure the datalabels is not undefined
    processedData.options.plugins.datalabels =
      processedData.options.plugins.datalabels || {};

    // Count labels based on chart type and hide if more than MAX_LABELS
    const MAX_LABELS = 30;
    let labelCount = 0;
    if (processedData.data) {
      if (processedData.type === "pie" || processedData.type === "doughnut") {
        // For pie/doughnut charts, count data points
        labelCount =
          processedData.data.datasets?.[0]?.data?.length ||
          processedData.data.labels?.length ||
          0;
      } else if (
        processedData.type === "line" ||
        processedData.type === "bar" ||
        processedData.type === "radar"
      ) {
        // For line/bar/radar charts, count labels or sum data points across datasets
        if (
          processedData.data.labels &&
          Array.isArray(processedData.data.labels)
        ) {
          labelCount = processedData.data.labels.length;
        } else if (
          processedData.data.datasets &&
          Array.isArray(processedData.data.datasets)
        ) {
          // Sum all data points across datasets
          labelCount = processedData.data.datasets.reduce(
            (sum: number, dataset: any) => sum + (dataset.data?.length || 0),
            0
          );
        }
      } else {
        // For other chart types, try to count labels or data points
        labelCount =
          processedData.data.labels?.length ||
          processedData.data.datasets?.[0]?.data?.length ||
          0;
      }
    }

    // Hide labels if there are more than MAX_LABELS
    if (labelCount > MAX_LABELS) {
      processedData.options.plugins.datalabels.display = false;
    }

    // Configure chart responsiveness — respect LLM-set aspect ratio when present
    processedData.options.responsive = true;
    if (processedData.options.aspectRatio != null) {
      processedData.options.maintainAspectRatio = true;
    } else {
      processedData.options.maintainAspectRatio = false;
    }

    // Minimize top and bottom padding around the chart (only if LLM didn't specify)
    processedData.options.layout = processedData.options.layout || {};
    processedData.options.layout.padding ??= 0;

    // add some point formatting to the chartData ONLY if the chart is a line chart.
    // bar and pie chart data anchors are centered and look good already.
    // Only set these if the LLM hasn't already provided them.
    if (processedData.type === "line") {
      processedData.options.plugins.datalabels.anchor ??= "end";
      processedData.options.plugins.datalabels.align ??= "top";
      processedData.options.plugins.datalabels.offset ??= 4;
    }

    // add the formatter to the chartData so data labels are abbreviated numbers with 2 decimal places.
    // Only set the formatter if the LLM hasn't already provided one.
    if (!processedData.options.plugins.datalabels.formatter) {
      processedData.options.plugins.datalabels.formatter = (value: any) => {
        // convert numbers to abbreviated numbers with 2 decimal places.
        if (typeof value === "number") {
          return value.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            notation: "compact",
          });
        }
        return value;
      };
    }

    // Default bottom padding for chart titles to keep them above data labels
    const DEFAULT_BOTTOM_PADDING = 30;

    // Add bottom padding to chart title only if LLM didn't specify one
    processedData.options.plugins.title ??= {};
    if (processedData.options.plugins.title.padding === undefined) {
      processedData.options.plugins.title.padding = {
        bottom: DEFAULT_BOTTOM_PADDING,
      };
    } else if (
      typeof processedData.options.plugins.title.padding === "number"
    ) {
      // LLM specified a number — convert to object, preserve the value for all sides
      const originalPadding = processedData.options.plugins.title.padding;
      processedData.options.plugins.title.padding = {
        top: originalPadding,
        right: originalPadding,
        left: originalPadding,
        bottom: originalPadding,
      };
    } else if (
      typeof processedData.options.plugins.title.padding === "object" &&
      processedData.options.plugins.title.padding !== null
    ) {
      // LLM specified an object — only fill in bottom if not already set
      processedData.options.plugins.title.padding.bottom ??=
        DEFAULT_BOTTOM_PADDING;
    }

    // Add onClick handler if metadata is available
    if (metadata?.cellMapping) {
      processedData.options.onClick = (
        event: any,
        elements: any[],
        chart: Chart
      ) => {
        if (elements.length === 0) return;

        const element = elements[0];
        const datasetIndex = element.datasetIndex;
        const index = element.index;
        const dataPointKey = `${datasetIndex}_${index}`;

        const cellRef = metadata.cellMapping[dataPointKey];
        if (!cellRef) return;

        // Extract the actual value from the clicked data point
        let cellValue: string | number | undefined = undefined;

        // Get value from chart data
        if (chart.data?.datasets?.[datasetIndex]?.data?.[index] !== undefined) {
          const rawValue = chart.data.datasets[datasetIndex].data[index];
          // Handle array values (for floating bars like waterfall charts)
          if (Array.isArray(rawValue)) {
            cellValue = rawValue[rawValue.length - 1]; // Use the end value
          } else {
            cellValue = rawValue;
          }
        }

        // Fallback: try to get from element's parsed value
        if (cellValue === undefined && element.parsed !== undefined) {
          if (typeof element.parsed === "object" && element.parsed !== null) {
            // For different chart types, parsed might have different structures
            cellValue =
              (element.parsed as any).y ??
              (element.parsed as any).r ??
              element.parsed;
          } else {
            cellValue = element.parsed;
          }
        }

        // Convert to string for originalNumValue (only if we have a value)
        const cellValueStr =
          cellValue !== undefined ? String(cellValue) : undefined;

        if (cellRef.citationId) {
          // Open citation
          window.dispatchEvent(
            new CustomEvent("openDocumentPanel", {
              detail: {
                citationId: cellRef.citationId,
                queryId: metadata.queryId,
                originalNumValue: cellValueStr, // Pass the cell value for highlighting
              },
            })
          );
        } else if (cellRef.isCalculator && cellRef.docId) {
          // Open Excel with cell highlight - use openDocumentPanel event to open side panel
          const cellRefStr = convertRowColToExcelRef(cellRef.row, cellRef.col);
          window.dispatchEvent(
            new CustomEvent("openDocumentPanel", {
              detail: {
                fileId: cellRef.docId,
                fileName: "Calculator Results",
                fileType: "excel",
                highlightCell: cellRefStr,
                originalNumValue: cellValueStr, // Pass the cell value for fallback highlighting
                highlightContext: undefined, // Explicitly clear to prevent table search
              },
            })
          );
        } else if (cellRef.docId) {
          // Open Excel with cell highlight (non-calculator) - use openDocumentPanel event to open side panel
          const cellRefStr = convertRowColToExcelRef(cellRef.row, cellRef.col);
          window.dispatchEvent(
            new CustomEvent("openDocumentPanel", {
              detail: {
                fileId: cellRef.docId,
                fileName: "Table Results",
                fileType: "excel",
                highlightCell: cellRefStr,
                originalNumValue: cellValueStr, // Pass the cell value for fallback highlighting
                highlightContext: undefined, // Explicitly clear to prevent table search
              },
            })
          );
        }
      };
    }

    const chart = new Chart(ref.current, processedData);
    return () => chart.destroy();
  }, [chartData, metadata, dispatch]);
  return <canvas ref={ref} className="w-full h-full" />;
};

export default ChartViewer;
